import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SEO = ({
  title,
  content,
  keywords,
  canonical,
  ogImage,
  ogType = 'website',
  structuredData,
  noindex = false,
  nofollow = false,
  children
}) => {
  const siteUrl = 'https://mqcapital.co.uk';
  const defaultImage = `${siteUrl}/og-image.jpg`;
  const location = useLocation();

  // Strip any existing '| MarQ Capital' or '- MarQ Capital' suffix to prevent double-appending
  const cleanTitle = title
    ? title
        .replace(/\s*\|\s*MarQ Capital\s*$/i, '')
        .replace(/\s*-\s*MarQ Capital\s*$/i, '')
        .trim()
    : null;
  const fullTitle = cleanTitle
    ? `${cleanTitle} | MarQ Capital`
    : 'MarQ Capital - Systematic Quantitative Strategies';
  const fullDescription =
    content ||
    'MarQ Capital - Systematic Quantitative Strategies for Global Markets';

  // Dynamic canonical: prefer explicit prop, fallback to current path (strips hash/query)
  const canonicalPath = canonical || location.pathname;
  const fullCanonical = `${siteUrl}${canonicalPath}`;
  const fullOgImage = ogImage ? `${siteUrl}${ogImage}` : defaultImage;

  // Robots: always explicit so crawlers never have to guess
  const robotsIndex = noindex ? 'noindex' : 'index';
  const robotsFollow = nofollow ? 'nofollow' : 'follow';
  const robotsContent = `${robotsIndex}, ${robotsFollow}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name='description' content={fullDescription} />
      {keywords && <meta name='keywords' content={keywords} />}
      <link rel='canonical' href={fullCanonical} />

      {/* Robots - always explicit */}
      <meta name='robots' content={robotsContent} />
      <meta name='googlebot' content={robotsContent} />

      {/* Open Graph */}
      <meta property='og:title' content={fullTitle} />
      <meta property='og:description' content={fullDescription} />
      <meta property='og:type' content={ogType} />
      <meta property='og:url' content={fullCanonical} />
      <meta property='og:image' content={fullOgImage} />
      <meta property='og:image:width' content='1200' />
      <meta property='og:image:height' content='630' />
      <meta property='og:site_name' content='MarQ Capital' />
      <meta property='og:locale' content='en_GB' />

      {/* Twitter Card */}
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={fullTitle} />
      <meta name='twitter:description' content={fullDescription} />
      <meta name='twitter:image' content={fullOgImage} />
      <meta name='twitter:site' content='@mqcapital' />
      <meta name='twitter:creator' content='@mqcapital' />

      {/* Additional SEO Meta Tags */}
      <meta name='rating' content='general' />
      <meta name='distribution' content='global' />
      <meta name='revisit-after' content='7 days' />
      <meta httpEquiv='content-language' content='en-GB' />
      <meta name='language' content='English' />

      {/* Financial Services Specific */}
      <meta name='author' content='MarQ Capital Limited' />
      <meta name='publisher' content='MarQ Capital Limited' />
      <meta
        name='subject'
        content='Financial Services, Quantitative Strategies, Algorithmic Trading'
      />
      <meta name='classification' content='Business' />
      <meta name='category' content='Finance' />

      {/* Geo Tags for UK/London */}
      <meta name='geo.region' content='GB' />
      <meta name='geo.placename' content='London, United Kingdom' />
      <meta name='ICBM' content='51.5074, -0.1278' />

      {/* Structured Data */}
      {structuredData && (
        <>
          {Array.isArray(structuredData) ? (
            structuredData.map((data, index) => (
              <script key={index} type='application/ld+json'>
                {JSON.stringify(data)}
              </script>
            ))
          ) : (
            <script type='application/ld+json'>
              {JSON.stringify(structuredData)}
            </script>
          )}
        </>
      )}

      {children}
    </Helmet>
  );
};

export default SEO;
